import { getPrimaryCohortRole } from './cohortSelection';

type CohortRole = 'mentor' | 'mentee' | 'cohort_director' | 'cohort_admin';

type RawAuthUser = {
  id: number;
  username: string;
  first_name?: string;
  last_name?: string;
  is_admin?: boolean;
  has_completed_intake?: boolean;
  profile_color?: string | null;
  debug_enabled?: boolean;
  profile?: {
    avatar_url?: string | null;
  } | null;
  organization?: {
    id: string;
    name: string;
    org_id: string;
    icon_url?: string | null;
  } | null;
  organization_role?: string;
  cohorts?: Array<{
    id: string;
    name: string;
    is_active?: boolean;
    is_current?: boolean;
    role?: string;
    mentee_label_singular?: string | null;
    mentee_label_plural?: string | null;
    mentor_label_singular?: string | null;
    mentor_label_plural?: string | null;
    start_date?: string | null;
    end_date?: string | null;
  }>;
  settings?: {
    question_flow_tts_enabled?: boolean;
    question_flow_tts_available?: boolean;
    voice_mode_voice?: string;
    voice_mode_personality?: string;
    voice_mode_response_length?: string;
    voice_mode_follow_up_intensity?: string;
  };
};

type OrganizationRole = 'user' | 'admin';

const normalizeRole = (role?: string): CohortRole | undefined => {
  if (!role) {
    return undefined;
  }

  return ['mentor', 'mentee', 'cohort_director', 'cohort_admin'].includes(role)
    ? (role as CohortRole)
    : undefined;
};

const deriveRole = (
  cohorts?: Array<{ role?: string; is_active?: boolean; is_current?: boolean }>,
): CohortRole | undefined => {
  return normalizeRole(getPrimaryCohortRole(cohorts));
};

const normalizeOrganizationRole = (role?: string): OrganizationRole =>
  role === 'admin' ? 'admin' : 'user';

export const normalizeAuthenticatedUser = (userData: RawAuthUser) => ({
  id: userData.id,
  username: userData.username,
  first_name: userData.first_name,
  last_name: userData.last_name,
  is_staff: Boolean(userData.is_admin),
  cohorts: Array.isArray(userData.cohorts)
    ? userData.cohorts.map((cohort) => ({
        id: cohort.id,
        name: cohort.name,
        is_active: Boolean(cohort.is_active),
        is_current: Boolean(cohort.is_current),
        role: normalizeRole(cohort.role) ?? 'mentee',
        mentee_label_singular: cohort.mentee_label_singular ?? null,
        mentee_label_plural: cohort.mentee_label_plural ?? null,
        mentor_label_singular: cohort.mentor_label_singular ?? null,
        mentor_label_plural: cohort.mentor_label_plural ?? null,
        start_date: cohort.start_date ?? null,
        end_date: cohort.end_date ?? null,
      }))
    : [],
  avatar_url:
    typeof userData?.profile?.avatar_url === 'string' || userData?.profile?.avatar_url === null
      ? userData.profile.avatar_url
      : null,
  organization: userData.organization
    ? {
        id: userData.organization.id,
        name: userData.organization.name,
        org_id: userData.organization.org_id,
        icon_url:
          typeof userData.organization.icon_url === 'string' || userData.organization.icon_url === null
            ? userData.organization.icon_url
            : null,
      }
    : null,
  organization_role: normalizeOrganizationRole(userData.organization_role),
  has_completed_intake: userData.has_completed_intake,
  profile_color:
    typeof userData.profile_color === 'string' || userData.profile_color === null
      ? userData.profile_color
      : null,
  debug_enabled: typeof userData.debug_enabled === 'boolean' ? userData.debug_enabled : undefined,
  settings: {
    question_flow_tts_enabled:
      typeof userData.settings?.question_flow_tts_enabled === 'boolean'
        ? userData.settings.question_flow_tts_enabled
        : false,
    question_flow_tts_available:
      typeof userData.settings?.question_flow_tts_available === 'boolean'
        ? userData.settings.question_flow_tts_available
        : false,
    voice_mode_voice:
      typeof userData.settings?.voice_mode_voice === 'string'
        ? userData.settings.voice_mode_voice
        : 'cedar',
    voice_mode_personality:
      typeof userData.settings?.voice_mode_personality === 'string'
        ? userData.settings.voice_mode_personality
        : 'warm_practical',
    voice_mode_response_length:
      typeof userData.settings?.voice_mode_response_length === 'string'
        ? userData.settings.voice_mode_response_length
        : 'balanced',
    voice_mode_follow_up_intensity:
      typeof userData.settings?.voice_mode_follow_up_intensity === 'string'
        ? userData.settings.voice_mode_follow_up_intensity
        : 'standard',
  },
  role: deriveRole(userData.cohorts),
});
